import { Cart, Location, Shop, Customer } from 'Types'
import { isShipping } from 'Utils/cartFulfillment'
import { isCategoryAllowed, CMP_CATEGORIES } from 'Utils/privacy'

/**
 * True when GA/GTM events may be sent. Without Marketing consent GTM never
 * loads and window.dataLayer may not even exist (dataLayer is a queue — items
 * pushed before consent would be sent later if the user grants consent
 * mid-session, so we must not push at all).
 */
const isDataLayerReady = (): boolean => isCategoryAllowed(CMP_CATEGORIES.MARKETING) && Array.isArray(window.dataLayer)

/**
 * Send 'order submitted' Google Analytics event for Sociavore
 */
export function sendGTMOrderSubmittedEvent(cart: Cart, location: Location, shop: Shop): void {
  if (!isDataLayerReady()) return

  const orderType = isShipping(cart.fulfillment) ? 'Delivery' : 'Pickup'

  const items = cart.cartItems.map((item) => {
    return {
      name: item.name,
      image_url: item.coverSerializableHash.url,
      category: item.categoryName || '',
      variant: item.variantName || '',
      price: item.piecePrice.toFixed(2),
      quantity: item.quantity,
      instructions: !!item.customerNote,
    }
  })

  window.dataLayer.push({
    event: 'scvr_event',
    event_properties: {
      event_name: 'order submitted',
      location: location.address,
      num_of_products: cart.cartItems.length,
      order_appfees_percent: `${shop.appFeePercentage}%`,
      order_appfees_value: cart.shopAppFeeMoney,
      items: items,
      currency: shop.currency,
      gift_code: cart.appliedGiftCards.length ? cart.appliedGiftCards[0].cardLast4 : '',
      order_subtotal: cart.totalPrice,
      order_discount: cart.totalDiscountMoney,
      order_salestax: cart.totalTaxMoney,
      order_total: cart.totalMoney,
      order_type: orderType,
      order_id: parseInt(cart.id),
    },
  })
}

/**
 * Send custom purchase Google Analytics event for customers
 */
export const sendCustomPurchaseEvent = (cart: Cart): void => {
  if (!isDataLayerReady()) return

  // Custom GA event
  window.dataLayer.push({
    event: 'ga_event',
    eventCategory: 'purchase',
    eventAction: 'new order',
    eventLabel: `new order, id: ${cart.id}`,
  })
}

/**
 * Send purchase UA event for customers
 * https://developers.google.com/analytics/devguides/collection/gtagjs/enhanced-ecommerce#measure_purchases
 */
export const sendGtagUAPurchaseEvent = (cart: Cart, location: Location): void => {
  if (!isCategoryAllowed(CMP_CATEGORIES.MARKETING)) return
  if (!window?.gtag) return

  const coupon = cart.hasAppliedDiscountCodes ? cart.appliedDiscountCodes.map(({ name }) => name).join(', ') : ''

  const items = cart.cartItems.map((item) => {
    return {
      item_id: item.id,
      item_name: item.name,
      affiliation: 'Online Ordering',
      item_brand: item.name,
      item_category: item.type || '',
      item_list_name: item.categoryName,
      item_variant: item.variantName || '',
      location_id: window?.VARIABLES?.locationId || 'default',
      price: item.totalPrice,
      quantity: item.quantity,
      upsell_item: item.upsellItem || false,
    }
  })

  gtag('event', 'purchase', {
    transaction_id: `T_${cart.id}`,
    value: cart.totalMoney,
    tax: cart.totalTaxAndFeesMoney,
    shipping: cart.totalDeliveryCostMoney,
    currency: window?.VARIABLES?.shopCurrency || 'CAD',
    coupon,
    discount: cart.totalDiscountMoney,
    location_name: location?.name || '',
    items,
  })
}

/**
 * Send account created Google Analytics event for customers
 */
export const sendGTMAccountCreatedEvent = (customer: Customer): void => {
  if (!isDataLayerReady()) return

  window.dataLayer.push({
    event: 'accountCreated',
    userId: customer.id,
    email: customer.email,
    phone: customer.phone,
    firstName: customer.firstName,
    lastName: customer.lastName,
  })

  window.dataLayer.push({
    event: 'ga_event',
    eventCategory: 'accountCreated',
    eventAction: 'new account',
    eventLabel: `new account, id: ${customer.id}`,
  })
}
